import * as React from "react";
import { useEffect, useState, useCallback } from "react";
import {
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Box,
  CircularProgress,
} from "@material-ui/core";
import { Add as AddIcon } from "@material-ui/icons";
import axios from "axios";
import { useNotify } from "react-admin";

import { ENDPOINTS } from "../apiConstants";
import { getConfigGet, getConfigPost, getConfigPatch } from "../getConfig";

interface FeatureFlag {
  id: number;
  feature: string;
  disable: number;
  description: string | null;
  from_date: string;
  to_date: string | null;
  createdBy: string | null;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
}

interface FeatureFlagResponse {
  enabled: string[];
  disabled: string[];
  flagDetails: FeatureFlag[];
}

const styles = {
  container: {
    padding: "20px",
    maxWidth: "1200px",
    margin: "0 auto",
  },
  card: {
    marginBottom: "20px",
  },
  tableContainer: {
    marginTop: "20px",
  },
  actionButtons: {
    display: "flex",
    gap: "10px",
    marginBottom: "20px",
    alignItems: "center",
  },
  dropdownContainer: {
    display: "flex",
    gap: "20px",
    alignItems: "center",
    marginBottom: "20px",
  },
  statusChip: {
    fontWeight: "bold" as const,
  },
  addButton: {
    marginBottom: "20px",
  },
  dialogContent: {
    padding: "20px",
  },
  formField: {
    marginBottom: "20px",
  },
};

const FeatureFlagDashboard = () => {
  const notify = useNotify();
  const [featureFlags, setFeatureFlags] = useState<FeatureFlagResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedFeature, setSelectedFeature] = useState<string>("");
  const [actionType, setActionType] = useState<"enable" | "disable">("disable");
  const [actionLoading, setActionLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [newFeature, setNewFeature] = useState({
    feature: "",
    description: "",
    disable: true,
    from_date: new Date().toISOString().split('T')[0],
  });

  const fetchFeatureFlags = useCallback(async () => {
    try {
      setLoading(true);
      const config = getConfigGet({});
      const response = await axios.get(ENDPOINTS.FEATURE_FLAG_DASHBOARD, config);
      setFeatureFlags(response.data as FeatureFlagResponse);
    } catch (err: any) {
      notify(err.response?.data?.message || "Failed to fetch feature flags", "error");
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    fetchFeatureFlags();
  }, [fetchFeatureFlags]);

  const handleFeatureAction = async () => {
    if (!selectedFeature) {
      notify("Please select a feature", "error");
      return;
    }

    try {
      setActionLoading(true);

      const config = getConfigPatch();
      const payload = {
        feature: selectedFeature,
        disable: actionType === "disable" ? 1 : 0,
        targeting: null,
      };

      await axios.patch(ENDPOINTS.FEATURE_FLAG_DASHBOARD, payload, { headers: config });
      notify(`Feature "${selectedFeature}" has been ${actionType}d successfully`, "info");
      setSelectedFeature("");
      await fetchFeatureFlags();
    } catch (err: any) {
      notify(err.response?.data?.message || `Failed to ${actionType} feature`, "error");
    } finally {
      setActionLoading(false);
    }
  };

  const handleCreateFeature = async () => {
    if (!newFeature.feature.trim()) {
      notify("Please enter a feature name", "error");
      return;
    }

    try {
      setActionLoading(true);

      const config = getConfigPost();
      const payload: any = {
        feature: newFeature.feature,
        disable: newFeature.disable ? 1 : 0,
        from_date: newFeature.from_date,
        targeting: null,
      };
      
      if (newFeature.description.trim()) {
        payload.description = newFeature.description;
      }

      await axios.post(ENDPOINTS.FEATURE_FLAG_DASHBOARD, payload, { headers: config });
      notify(`Feature "${newFeature.feature}" has been created successfully`, "info");
      setNewFeature({
        feature: "",
        description: "",
        disable: true,
        from_date: new Date().toISOString().split('T')[0],
      });
      setOpenDialog(false);
      await fetchFeatureFlags();
    } catch (err: any) {
      notify(err.response?.data?.message || "Failed to create feature", "error");
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusChip = (disable: number) => {
    return disable === 1 ? (
      <Chip label="Disabled" color="secondary" style={styles.statusChip} />
    ) : (
      <Chip label="Enabled" color="primary" style={styles.statusChip} />
    );
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <Typography variant="h4" gutterBottom>
        Feature Flag Dashboard
      </Typography>

      {/* Create New Feature Button */}
      <div style={styles.addButton}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => setOpenDialog(true)}
        >
          Create New Feature
        </Button>
      </div>

      {/* Feature Action Controls */}
      <Card style={styles.card}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Feature Actions
          </Typography>
          <div style={styles.dropdownContainer}>
            <FormControl style={{ minWidth: 200 }}>
              <InputLabel>Select Feature</InputLabel>
              <Select
                value={selectedFeature}
                onChange={(e) => setSelectedFeature(e.target.value as string)}
              >
                {actionType === "disable" && featureFlags?.enabled?.map((feature) => (
                  <MenuItem key={feature} value={feature}>
                    {feature}
                  </MenuItem>
                ))}
                {actionType === "enable" && featureFlags?.disabled?.map((feature) => (
                  <MenuItem key={feature} value={feature}>
                    {feature}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl style={{ minWidth: 150 }}>
              <InputLabel>Action</InputLabel>
              <Select
                value={actionType}
                onChange={(e) => {
                  setActionType(e.target.value as "enable" | "disable");
                  setSelectedFeature("");
                }}
              >
                <MenuItem value="disable">Disable</MenuItem>
                <MenuItem value="enable">Enable</MenuItem>
              </Select>
            </FormControl>

            <Button
              variant="contained"
              color={actionType === "disable" ? "secondary" : "primary"}
              onClick={handleFeatureAction}
              disabled={!selectedFeature || actionLoading}
            >
              {actionLoading ? <CircularProgress size={20} /> : `${actionType.charAt(0).toUpperCase() + actionType.slice(1)} Feature`}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Feature Flags Table */}
      <Card style={styles.card}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Feature Flag Details
          </Typography>
          <TableContainer component={Paper} style={styles.tableContainer}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Feature</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>From Date</TableCell>
                  <TableCell>To Date</TableCell>
                  <TableCell>Created By</TableCell>
                  <TableCell>Updated By</TableCell>
                  <TableCell>Created At</TableCell>
                  <TableCell>Updated At</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {featureFlags?.flagDetails?.map((flag) => (
                  <TableRow key={flag.id}>
                    <TableCell>{flag.id}</TableCell>
                    <TableCell>{flag.feature}</TableCell>
                    <TableCell style={{ maxWidth: "300px", wordBreak: "break-word" }}>{flag.description || "N/A"}</TableCell>
                    <TableCell>{getStatusChip(flag.disable)}</TableCell>
                    <TableCell>{formatDate(flag.from_date)}</TableCell>
                    <TableCell>{formatDate(flag.to_date)}</TableCell>
                    <TableCell>{flag.createdBy || "N/A"}</TableCell>
                    <TableCell>{flag.updatedBy || "N/A"}</TableCell>
                    <TableCell>{formatDate(flag.createdAt)}</TableCell>
                    <TableCell>{formatDate(flag.updatedAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Create New Feature Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Feature Flag</DialogTitle>
        <DialogContent style={styles.dialogContent}>
          <TextField
            fullWidth
            label="Feature Name"
            value={newFeature.feature}
            onChange={(e) => setNewFeature({ ...newFeature, feature: e.target.value })}
            style={styles.formField}
            placeholder="Enter feature name"
          />

          <TextField
            fullWidth
            label="Description"
            value={newFeature.description}
            onChange={(e) => setNewFeature({ ...newFeature, description: e.target.value })}
            style={styles.formField}
            placeholder="Enter description (optional)"
            multiline
            rows={2}
          />
          
          <FormControl fullWidth style={styles.formField}>
            <InputLabel>Initial Status</InputLabel>
            <Select
              value={newFeature.disable}
              onChange={(e) => setNewFeature({ ...newFeature, disable: e.target.value as boolean })}
            >
              <MenuItem value={false as any}>Enabled</MenuItem>
              <MenuItem value={true as any}>Disabled</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="From Date"
            type="date"
            value={newFeature.from_date}
            onChange={(e) => setNewFeature({ ...newFeature, from_date: e.target.value })}
            style={styles.formField}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateFeature}
            color="primary"
            variant="contained"
            disabled={actionLoading}
          >
            {actionLoading ? <CircularProgress size={20} /> : "Create Feature"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default FeatureFlagDashboard;
